import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { axiosInstance } from "../../axios/axiosInstance";
import { useAuth } from "../../Context/AuthContext";
import "../../assets/css/itemReference.css";

const MAX_LENGTH = 150;
const SIZE_DURATION = 240;
const EDIT_DESIGN = "inline";

const sharedReferences = new Map();
const referenceListeners = new Set();

const subscribeReferences = (listener) => {
  referenceListeners.add(listener);
  return () => referenceListeners.delete(listener);
};

const publishReference = (key, base, reference) => {
  sharedReferences.set(key, { base, reference });
  referenceListeners.forEach((listener) => listener());
};

const useItemReference = (item) => {
  const { userId } = useAuth();
  const referenceKey = `${item.order_id}:${item.prod_id}`;
  const baseReference = item.item_reference ?? "";
  const shared = useSyncExternalStore(subscribeReferences, () => sharedReferences.get(referenceKey));
  const currentReference = shared && shared.base === baseReference ? shared.reference : baseReference;
  const [saved, setSaved] = useState(currentReference);
  const [value, setValue] = useState(currentReference);
  const [editing, setEditing] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const cancelRef = useRef(false);
  const savingRef = useRef(false);
  const editingRef = useRef(false);

  useEffect(() => {
    setSaved(currentReference);
    if (!editingRef.current) setValue(currentReference);
  }, [currentReference]);

  const closeEditor = () => {
    editingRef.current = false;
    setEditing(false);
    setAnimateIn(true);
  };

  const openEditor = () => {
    setError("");
    setValue(saved);
    editingRef.current = true;
    setEditing(true);
  };

  const cancel = () => {
    cancelRef.current = true;
  };

  const discard = () => {
    if (!editingRef.current || savingRef.current) return;
    setValue(saved);
    closeEditor();
  };

  const save = async (e, override) => {
    if (!editingRef.current || savingRef.current || e?.relatedTarget?.dataset?.refCancel !== undefined) return;
    if (cancelRef.current) {
      cancelRef.current = false;
      setValue(saved);
      closeEditor();
      return;
    }
    const reference = override ?? value.trim();
    if (reference === saved) {
      closeEditor();
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      await axiosInstance.post("MyCart/UpdateItemReference", {
        userId: String(userId),
        orderId: String(item.order_id),
        productId: String(item.prod_id),
        reference,
      });
      setSaved(reference);
      setValue(reference);
      publishReference(referenceKey, baseReference, reference);
    } catch (e) {
      setError(e?.response?.data?.message || "Unable to save reference. Please try again.");
      setValue(saved);
    } finally {
      savingRef.current = false;
      setSaving(false);
      closeEditor();
    }
  };

  const remove = () => save(undefined, "");

  return { saved, value, setValue, editing, animateIn, saving, error, openEditor, cancel, discard, save, remove };
};

const ItemReferenceTrigger = ({ reference, readOnly, className = "" }) => {
  if (readOnly || reference.saved || reference.editing) return null;
  return (
    <button
      type="button"
      className={`item-ref-add ${reference.animateIn ? "is-entering" : ""} ${className}`}
      onClick={reference.openEditor}
    >
      <i className="fa-solid fa-tag" />
      Add reference
    </button>
  );
};

const ReferenceTextarea = ({
  value,
  setValue,
  saving,
  save,
  cancel,
  discard,
  saveOnBlur = true,
  extraLine = true,
  placeholder = "Add reference",
}) => {
  const textareaRef = useRef(null);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = extraLine ? parseFloat(getComputedStyle(el).lineHeight) || 0 : 0;
    const borders = el.offsetHeight - el.clientHeight;
    el.style.height = `${el.scrollHeight + lineHeight + borders}px`;
  }, [value, extraLine]);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  return (
    <textarea
      ref={textareaRef}
      rows={1}
      className="item-ref-textarea"
      placeholder={placeholder}
      aria-label="Item reference"
      maxLength={MAX_LENGTH}
      value={value}
      disabled={saving}
      onChange={(e) => setValue(e.target.value.replace(/[\r\n]+/g, " "))}
      onBlur={saveOnBlur ? save : discard}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          if (saveOnBlur) e.currentTarget.blur();
        }
        if (e.key === "Escape") {
          if (saveOnBlur) cancel();
          e.currentTarget.blur();
        }
      }}
    />
  );
};

const useSizeTransition = (ref, trigger) => {
  const lastSizeRef = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      lastSizeRef.current = null;
      return undefined;
    }
    const from = lastSizeRef.current;
    el.style.width = "";
    el.style.height = "";
    const { width, height } = el.getBoundingClientRect();
    lastSizeRef.current = { width, height };
    if (!from || (Math.abs(from.width - width) < 1 && Math.abs(from.height - height) < 1)) return undefined;

    el.style.width = `${from.width}px`;
    el.style.height = `${from.height}px`;
    el.getBoundingClientRect();
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;
    const timer = setTimeout(() => {
      el.style.width = "";
      el.style.height = "";
    }, SIZE_DURATION + 50);
    return () => clearTimeout(timer);
  }, [ref, trigger]);
};

const ItemReferencePanel = ({ reference, readOnly, className = "" }) => {
  const { saved, value, setValue, editing, saving, error, openEditor, cancel, discard, save, remove } = reference;
  const chipRef = useRef(null);
  useSizeTransition(chipRef, editing);
  const showDelete = !!saved && value.trim() === saved;
  const actionMode = editing ? (showDelete ? "delete" : "save") : null;
  const [lastActionMode, setLastActionMode] = useState(actionMode);
  const [flipAction, setFlipAction] = useState(false);
  if (actionMode !== lastActionMode) {
    setLastActionMode(actionMode);
    setFlipAction(lastActionMode !== null && actionMode !== null);
  }
  if (!editing && !saved && !error) return null;
  const atLimit = value.length >= MAX_LENGTH;
  const canEdit = !readOnly && !editing && !!saved;
  const inlineEditing = editing && EDIT_DESIGN === "inline";
  const classicEditing = editing && !inlineEditing;
  const actionClass = `item-ref-save${flipAction ? " is-flipping" : ""}`;
  const openOnClick = () => {
    if (window.getSelection()?.toString()) return;
    openEditor();
  };
  const chipClass = [
    "item-ref-note",
    editing ? "item-ref-note-editing" : "",
    editing ? `is-editing edit-${EDIT_DESIGN}` : "",
    canEdit ? "is-editable" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={className}>
      <div ref={chipRef} className={chipClass} onClick={canEdit ? openOnClick : undefined}>
        {inlineEditing ? (
          <>
            <div className="item-ref-box">
              <span className="item-ref-label">Reference</span>
              <div className="item-ref-field">
                <ReferenceTextarea
                  value={value}
                  setValue={setValue}
                  saving={saving}
                  save={save}
                  cancel={cancel}
                  discard={discard}
                  saveOnBlur={false}
                  extraLine={false}
                  placeholder={`Enter reference (max ${MAX_LENGTH} characters)`}
                />
              </div>
              {saving && <i className="fa-solid fa-spinner fa-spin item-ref-count" />}
            </div>
            {showDelete ? (
              <button
                key="delete"
                type="button"
                className={`${actionClass} item-ref-delete`}
                aria-label="Delete reference"
                title="Delete"
                disabled={saving}
                onMouseDown={(e) => e.preventDefault()}
                onClick={remove}
              >
                <i className="fa-regular fa-trash-can item-ref-btn-icon" />
                <span className="item-ref-btn-text">Delete</span>
              </button>
            ) : (
              <button
                key="save"
                type="button"
                className={actionClass}
                aria-label="Save reference"
                title="Save"
                disabled={saving}
                onMouseDown={(e) => e.preventDefault()}
                onClick={save}
              >
                <i className="fa-solid fa-check item-ref-btn-icon" />
                <span className="item-ref-btn-text">Save</span>
              </button>
            )}
            <button
              type="button"
              className="item-ref-cancel"
              data-ref-cancel=""
              disabled={saving}
              onMouseDown={(e) => e.preventDefault()}
              onClick={discard}
              aria-label="Cancel"
              title="Cancel"
            >
              <i className="fa-solid fa-xmark item-ref-btn-icon" />
              <span className="item-ref-btn-text">Cancel</span>
            </button>
            {atLimit && (
              <span className="item-ref-limit" role="alert">
                <i className="fa-solid fa-circle-exclamation" />
                Maximum {MAX_LENGTH} characters reached
              </span>
            )}
          </>
        ) : classicEditing ? (
          <span className="item-ref-edit" aria-hidden="true">
            <i className="fa-regular fa-pen-to-square" />
          </span>
        ) : (
          canEdit && (
            <button type="button" className="item-ref-edit" aria-label="Edit reference" onClick={openEditor}>
              <i className="fa-regular fa-pen-to-square" />
            </button>
          )
        )}
        {classicEditing ? (
          <div className="item-ref-field">
            <ReferenceTextarea value={value} setValue={setValue} saving={saving} save={save} cancel={cancel} />
          </div>
        ) : (
          !editing &&
          saved && (
            <span className="item-ref-note-text" title={saved}>
              {saved}
            </span>
          )
        )}
        {classicEditing && (
          <div className="item-ref-footer">
            {atLimit && (
              <span className="item-ref-limit" role="alert">
                <i className="fa-solid fa-circle-exclamation" />
                Maximum {MAX_LENGTH} characters reached
              </span>
            )}
            <button
              type="button"
              className="item-ref-cancel"
              data-ref-cancel=""
              disabled={saving}
              onMouseDown={(e) => e.preventDefault()}
              onClick={discard}
            >
              <i className="fa-solid fa-xmark" />
              Cancel
            </button>
            {saving ? (
              <i className="fa-solid fa-spinner fa-spin item-ref-count" />
            ) : (
              <span className={`item-ref-count ${atLimit ? "is-max" : ""}`}>
                {value.length}/{MAX_LENGTH}
              </span>
            )}
          </div>
        )}
        {error && (
          <div className="item-ref-error" role="alert">
            <i className="fa-solid fa-circle-exclamation" />
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export const ItemReferenceInput = ({ item, readOnly = false, className = "mt-1" }) => {
  const reference = useItemReference(item);
  if (readOnly && !reference.saved) return null;
  return (
    <div className={className}>
      <ItemReferenceTrigger reference={reference} readOnly={readOnly} />
      <ItemReferencePanel reference={reference} readOnly={readOnly} />
    </div>
  );
};
